import { ApiError } from "@khazzin/api-client";
import { useCallback, useMemo, useRef, useState } from "react";

/**
 * One form pattern, whose whole job is to put the server's rejection on the
 * right input.
 *
 * No form library. What a screen in this product actually needs is a bag of
 * values, a dirty flag, and a rule for turning an `ApiError` into a message
 * under a field — and the third is the only hard part. A library brings
 * schemas, resolvers and a validation lifecycle for the two of those it does
 * not solve, and solves the third by not knowing about it.
 *
 * **The server's `field` is the wiring.** `14/03` §3 puts a `field` on the
 * error envelope precisely so a client can do this: a duplicate SKU comes back
 * as a 409 naming `sku`, and it lands under the SKU input rather than in a
 * banner that says "conflict" and leaves somebody to guess which of eleven
 * inputs caused it.
 *
 * **A 409 with a field is a 400 with a field.** They are different things to
 * the server — one is "this is malformed", the other "this collides with
 * something that already exists" — and the same thing to the person: the value
 * in that box is wrong, change it. Treating them differently would put the
 * duplicate SKU in a banner for no reason a user could name.
 *
 * **Except `version_conflict`, which is nobody's typo.** Somebody else saved
 * while this form was open. No field is at fault and changing one would not
 * help; the only useful action is to reload and see their version, so it goes
 * to the form-level banner with that action attached.
 */

/** Errors, keyed by field name. `_form` holds anything that is not one field's. */
export type FormErrors = Record<string, string>;

/** The key the banner reads. Not a legal field name, so it cannot collide. */
export const FORM_ERROR = "_form";

export interface ApiFormOptions<T extends object> {
	initial: T;
	/**
	 * Client-side checks, run before submitting.
	 *
	 * Returns errors by field name. Deliberately not a schema: the server
	 * validates everything anyway, and the only checks worth duplicating are the
	 * ones that save a round trip on something obvious — a required field left
	 * empty, a quantity below zero.
	 */
	validate?: (values: T) => FormErrors;
	submit: (values: T) => Promise<void>;
}

export interface ApiForm<T extends object> {
	values: T;
	/** Sets one field, clearing its error — the person is fixing it. */
	set: <K extends keyof T>(key: K, value: T[K]) => void;
	errors: FormErrors;
	submitting: boolean;
	/** Whether anything has been changed since the last reset. */
	dirty: boolean;
	handleSubmit: (event?: { preventDefault: () => void }) => void;
	reset: (values?: T) => void;
	/** Places a rejection on the field it names, or in the banner. */
	setServerError: (error: unknown) => void;
	/** True when the banner's error is one a reload would resolve. */
	staleVersion: boolean;
	/** Called by `FormField` so a server error can move focus to the input. */
	registerField: (name: string, element: HTMLElement | null) => void;
}

export function useApiForm<T extends object>({
	initial,
	validate,
	submit,
}: ApiFormOptions<T>): ApiForm<T> {
	const [values, setValues] = useState<T>(initial);
	const [errors, setErrors] = useState<FormErrors>({});
	const [submitting, setSubmitting] = useState(false);
	const [dirty, setDirty] = useState(false);
	const [staleVersion, setStaleVersion] = useState(false);

	// The inputs, so a rejection can move focus to the one it names. A ref
	// rather than state: registering must not re-render the form.
	const fields = useRef(new Map<string, HTMLElement>());

	const registerField = useCallback((name: string, element: HTMLElement | null) => {
		if (element === null) {
			fields.current.delete(name);
		} else {
			fields.current.set(name, element);
		}
	}, []);

	const set = useCallback<ApiForm<T>["set"]>((key, value) => {
		setValues((current) => ({ ...current, [key]: value }));
		setDirty(true);
		// The error goes the moment the person starts fixing it. Leaving it
		// until the next submit means a red field under a value that is now
		// correct, which reads as the form not listening.
		setErrors((current) => {
			if (current[key as string] === undefined && current[FORM_ERROR] === undefined) {
				return current;
			}
			const { [key as string]: _cleared, [FORM_ERROR]: _banner, ...rest } = current;
			return rest;
		});
		setStaleVersion(false);
	}, []);

	const setServerError = useCallback(
		(error: unknown) => {
			if (!(error instanceof ApiError)) {
				// A network failure or a bug. Nothing names a field, and the message
				// would be a stack trace, so the banner gets the generic one.
				setErrors({ [FORM_ERROR]: GENERIC_FAILURE });
				setStaleVersion(false);
				return;
			}

			// Somebody else saved while this form was open. No field is at fault.
			if (error.code === "version_conflict") {
				setErrors({ [FORM_ERROR]: error.message });
				setStaleVersion(true);
				return;
			}

			const field = error.field;
			setStaleVersion(false);

			if (field === null) {
				setErrors({ [FORM_ERROR]: error.message });
				return;
			}

			// A field this form does not have — `lines[0].quantity` from a bulk
			// endpoint, or a column the server knows about and this screen does
			// not. Putting it under no input at all would lose it, so the banner
			// carries the name: somebody reading "lines[0].quantity: must be
			// positive" can act on it, and "invalid argument" alone cannot.
			//
			// A rendered `FormField` of that name is a field this form has, even
			// when it is not a key of the values: a line's `lines[2].qty` lives
			// inside an array, and its error belongs under that input (EP10-S05).
			if (!(field in values) && !fields.current.has(field)) {
				setErrors({ [FORM_ERROR]: `${field}: ${error.message}` });
				return;
			}

			setErrors({ [field]: error.message });
			// Focus follows the error. Without it somebody who submitted a long
			// form from the footer is told something is wrong somewhere above the
			// fold, with no indication of where.
			fields.current.get(field)?.focus();
		},
		[values],
	);

	const handleSubmit = useCallback(
		(event?: { preventDefault: () => void }) => {
			event?.preventDefault();

			const found = validate?.(values) ?? {};
			if (Object.keys(found).length > 0) {
				setErrors(found);
				const first = Object.keys(found)[0];
				if (first !== undefined) {
					fields.current.get(first)?.focus();
				}
				return;
			}

			setErrors({});
			setStaleVersion(false);
			setSubmitting(true);

			void submit(values)
				.then(() => setDirty(false))
				.catch(setServerError)
				.finally(() => setSubmitting(false));
		},
		[values, validate, submit, setServerError],
	);

	const reset = useCallback(
		(next?: T) => {
			setValues(next ?? initial);
			setErrors({});
			setDirty(false);
			setStaleVersion(false);
		},
		[initial],
	);

	return useMemo(
		() => ({
			values,
			set,
			errors,
			submitting,
			dirty,
			handleSubmit,
			reset,
			setServerError,
			staleVersion,
			registerField,
		}),
		[
			values,
			set,
			errors,
			submitting,
			dirty,
			handleSubmit,
			reset,
			setServerError,
			staleVersion,
			registerField,
		],
	);
}

/**
 * What the banner says when the failure carries no message worth showing.
 *
 * A message key rather than a sentence: `FormErrorSummary` translates it, and
 * every other string in a form comes from a bundle. The server's own messages
 * are passed through as-is because they are already in the locale the client
 * asked for — that is what the `Accept-Language` header is for.
 */
export const GENERIC_FAILURE = "errors.internal";
